import React from 'react';
import { Navbar, Nav, Container, NavDropdown } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import useEquipmentPastDue from '../../hooks/useEquipmentPastDue';

// Minimal chrome for the standalone tablets site (tablets.* hostname):
// no portal sidebar or sections, just the board and its pages.
// On phones the page links move to a fixed bottom tab bar (app-style) and the
// top bar keeps only the brand, theme toggle and account menu; the inline
// links reappear on wider screens (the bottom bar hides via tablets.css).
const TabletsKioskBar = () => {
  const { user, logout, canAccessDriverTablets, canSeeTabletTab, canAccessDoors } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const showPod = canAccessDriverTablets() && canSeeTabletTab('driverPod');
  const showRamps = canAccessDriverTablets() && canSeeTabletTab('equipment');
  const rampsPastDue = useEquipmentPastDue(showRamps);
  const showMap = canAccessDriverTablets() && canSeeTabletTab('liveMap');
  const showPay = canAccessDriverTablets() && canSeeTabletTab('paySheets');
  const showPayroll = canAccessDriverTablets() && canSeeTabletTab('driverPayroll');
  // Doors is its own permission, so a user can have it without Driver Tablets;
  // they get just the Doors tab (the board pages would be forbidden).
  const showDoors = canAccessDoors();
  const showBoard = canAccessDriverTablets();
  const home = showBoard ? '/driver-tablets' : '/driver-tablets/doors';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const tabs = [
    // Driver detail/trip pages are reached from the board, so they light up Board.
    ...(showBoard
      ? [
        {
          to: '/driver-tablets',
          label: 'Board',
          icon: 'bi-kanban',
          activeIcon: 'bi-kanban-fill',
          active: location.pathname === '/driver-tablets' || location.pathname.startsWith('/driver-tablets/driver/')
        }
      ]
      : []),
    ...(showMap
      ? [{ to: '/driver-tablets/map', label: 'Map', icon: 'bi-geo-alt', activeIcon: 'bi-geo-alt-fill' }]
      : []),
    ...(showBoard
      ? [
        { to: '/driver-tablets/drivers', label: 'Drivers', icon: 'bi-people', activeIcon: 'bi-people-fill' },
        { to: '/driver-tablets/loads', label: 'Loads', icon: 'bi-list-ul' }
      ]
      : []),
    ...(showPod
      ? [{ to: '/driver-tablets/pod', label: 'POD', icon: 'bi-file-earmark-text', activeIcon: 'bi-file-earmark-text-fill' }]
      : []),
    ...(showBoard
      ? [{ to: '/driver-tablets/docs', label: 'Docs', icon: 'bi-folder2', activeIcon: 'bi-folder2-open' }]
      : []),
    ...(showRamps
      ? [{ to: '/driver-tablets/ramps', label: 'Ramps', icon: 'bi-tools', alert: rampsPastDue }]
      : []),
    ...(showPay
      ? [{ to: '/driver-tablets/pay', label: 'Pay', icon: 'bi-cash-stack' }]
      : []),
    ...(showDoors
      ? [{ to: '/driver-tablets/doors', label: 'Doors', icon: 'bi-door-open', activeIcon: 'bi-door-open-fill' }]
      : []),
    ...(showBoard
      ? [
        { to: '/driver-tablets/pcmiler', label: 'Miler', icon: 'bi-signpost-split', activeIcon: 'bi-signpost-split-fill' },
        { to: '/driver-tablets/manage', label: 'Manage', icon: 'bi-gear', activeIcon: 'bi-gear-fill' }
      ]
      : [])
  ];

  return (
    <>
      <Navbar bg="dark" variant="dark" expand="md" sticky="top" className="py-1 tablets-kiosk-bar">
        <Container fluid>
          <Navbar.Brand as={Link} to={home}>
            <i className="bi bi-truck me-1 me-md-2"></i>
            <span className="d-none d-md-inline">Driver Tablets</span>
            <span className="d-md-none">Tablets</span>
          </Navbar.Brand>
          <Navbar.Collapse id="kiosk-nav" className="tablets-kiosk-links">
            <Nav className="me-auto">
              {showBoard && <Nav.Link as={Link} to="/driver-tablets">Board</Nav.Link>}
              {showMap && <Nav.Link as={Link} to="/driver-tablets/map">Map</Nav.Link>}
              {showBoard && <Nav.Link as={Link} to="/driver-tablets/drivers">Drivers</Nav.Link>}
              {showBoard && <Nav.Link as={Link} to="/driver-tablets/loads">Loads</Nav.Link>}
              {showPod && <Nav.Link as={Link} to="/driver-tablets/pod">Driver POD</Nav.Link>}
              {showBoard && <Nav.Link as={Link} to="/driver-tablets/docs">Driver Docs</Nav.Link>}
              {showRamps && (
                <Nav.Link as={Link} to="/driver-tablets/ramps" className="tablets-tab-ramps">
                  Ramps
                  {rampsPastDue > 0 && (
                    <span className="tablets-tab-alert" title={`${rampsPastDue} past due`}>{rampsPastDue}</span>
                  )}
                </Nav.Link>
              )}
              {showPay && <Nav.Link as={Link} to="/driver-tablets/pay">Pay Sheets</Nav.Link>}
              {showPayroll && <Nav.Link as={Link} to="/driver-tablets/payroll">Payroll</Nav.Link>}
              {showDoors && <Nav.Link as={Link} to="/driver-tablets/doors">Doors</Nav.Link>}
              {showBoard && <Nav.Link as={Link} to="/driver-tablets/pcmiler">PC Miler</Nav.Link>}
              {showBoard && <Nav.Link as={Link} to="/driver-tablets/manage">Manage</Nav.Link>}
            </Nav>
          </Navbar.Collapse>
          <Nav className="ms-auto flex-row align-items-center gap-1">
            <Nav.Link className="px-2" onClick={toggleTheme} title="Toggle theme">
              <i className={`bi ${isDark ? 'bi-sun' : 'bi-moon'}`}></i>
            </Nav.Link>
            <NavDropdown
              title={
                <>
                  <span className="d-none d-md-inline">Hello {user?.username}!</span>
                  <i className="bi bi-person-circle d-md-none tablets-kiosk-usericon" aria-label={user?.username || 'Account'} />
                </>
              }
              align="end"
              id="kiosk-user"
              className="tablets-kiosk-user"
            >
              {/* Phones: pages that don't fit in the bottom tab bar */}
              {showPayroll && (
                <NavDropdown.Item as={Link} to="/driver-tablets/payroll" className="d-md-none">
                  <i className="bi bi-wallet2 me-2"></i>Payroll
                </NavDropdown.Item>
              )}
              {showPayroll && <NavDropdown.Divider className="d-md-none" />}
              <NavDropdown.Item onClick={handleLogout}>
                <i className="bi bi-box-arrow-right me-2"></i>Log off
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Container>
      </Navbar>

      <nav className={`tablets-bottomnav ${tabs.length > 8 ? 'tablets-bottomnav--tight' : ''} ${tabs.length > 10 ? 'tablets-bottomnav--tighter' : ''}`} aria-label="Tablets pages">
        {tabs.map(t => {
          const active = t.active ?? (location.pathname === t.to);
          return (
            <Link key={t.to} to={t.to} className={`tablets-bottomnav-item ${active ? 'active' : ''}`}>
              <i className={`bi ${active ? (t.activeIcon || t.icon) : t.icon}`} />
              {t.alert > 0 && <span className="tablets-tab-alert" title={`${t.alert} past due`}>{t.alert}</span>}
              <span>{t.label}</span>
            </Link>
          );
        })}
      </nav>
    </>
  );
};

export default TabletsKioskBar;
